import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const DayRatePricing = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOnboarding = searchParams.get('onboarding') === 'true';
  const [acceptsDayBookings, setAcceptsDayBookings] = useState(false);
  const [halfDayRates, setHalfDayRates] = useState({
    tier1MaxMileage: '',
    tier1Rate: '',
    tier2MaxMileage: '',
    tier2Rate: '',
    tier3MaxMileage: '',
    tier3Rate: ''
  });
  const [fullDayRates, setFullDayRates] = useState({
    tier1MaxMileage: '',
    tier1Rate: '',
    tier2MaxMileage: '',
    tier2Rate: '',
    tier3MaxMileage: '',
    tier3Rate: ''
  });
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchDayRates();
  }, []);

  const fetchDayRates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch acceptance status
      const statusResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/acceptance-status`, authHeaders);
      setAcceptsDayBookings(statusResponse.data.accepts_day_bookings);

      // Fetch rates
      const ratesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/rates`, authHeaders);

      // Populate half-day rates
      if (ratesResponse.data.rates.half_day && ratesResponse.data.rates.half_day.length === 3) {
        const halfDay = ratesResponse.data.rates.half_day;
        setHalfDayRates({
          tier1MaxMileage: halfDay[0].max_mileage.toString(),
          tier1Rate: halfDay[0].flat_rate.toString(),
          tier2MaxMileage: halfDay[1].max_mileage.toString(),
          tier2Rate: halfDay[1].flat_rate.toString(),
          tier3MaxMileage: halfDay[2].max_mileage.toString(),
          tier3Rate: halfDay[2].flat_rate.toString()
        });
      }

      // Populate full-day rates
      if (ratesResponse.data.rates.full_day && ratesResponse.data.rates.full_day.length === 3) {
        const fullDay = ratesResponse.data.rates.full_day;
        setFullDayRates({
          tier1MaxMileage: fullDay[0].max_mileage.toString(),
          tier1Rate: fullDay[0].flat_rate.toString(),
          tier2MaxMileage: fullDay[1].max_mileage.toString(),
          tier2Rate: fullDay[1].flat_rate.toString(),
          tier3MaxMileage: fullDay[2].max_mileage.toString(),
          tier3Rate: fullDay[2].flat_rate.toString()
        });
      }

    } catch (error) {
      console.error('Error fetching day rates:', error);
      setMessage({ type: 'error', text: 'Failed to load day rate settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAcceptance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/toggle-acceptance`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );
      setAcceptsDayBookings(response.data.accepts_day_bookings);
      setMessage({
        type: 'success',
        text: response.data.message
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error toggling acceptance:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update settings' });
    }
  };

  const validateRates = (rates, rateType) => {
    // Check all fields are filled
    const allFields = Object.values(rates);
    if (allFields.some(val => !val || val === '')) {
      return `All ${rateType} rate fields must be filled`;
    }

    // Convert to numbers
    const tier1Max = parseFloat(rates.tier1MaxMileage);
    const tier2Max = parseFloat(rates.tier2MaxMileage);
    const tier3Max = parseFloat(rates.tier3MaxMileage);

    // Check mileage tiers are in ascending order
    if (tier2Max <= tier1Max) {
      return 'Tier 2 max mileage must be greater than Tier 1';
    }
    if (tier3Max <= tier2Max) {
      return 'Tier 3 max mileage must be greater than Tier 2';
    }

    // Check all values are positive
    if (allFields.some(val => parseFloat(val) <= 0)) {
      return 'All values must be greater than zero';
    }

    return null;
  };

  const handleSaveHalfDay = async () => {
    const error = validateRates(halfDayRates, 'half-day');
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/rates`, {
        rate_type: 'half_day',
        tier_1_max_mileage: parseFloat(halfDayRates.tier1MaxMileage),
        tier_1_flat_rate: parseFloat(halfDayRates.tier1Rate),
        tier_2_max_mileage: parseFloat(halfDayRates.tier2MaxMileage),
        tier_2_flat_rate: parseFloat(halfDayRates.tier2Rate),
        tier_3_max_mileage: parseFloat(halfDayRates.tier3MaxMileage),
        tier_3_flat_rate: parseFloat(halfDayRates.tier3Rate)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Half-day rates saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving half-day rates:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save rates' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveFullDay = async () => {
    const error = validateRates(fullDayRates, 'full-day');
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/rates`, {
        rate_type: 'full_day',
        tier_1_max_mileage: parseFloat(fullDayRates.tier1MaxMileage),
        tier_1_flat_rate: parseFloat(fullDayRates.tier1Rate),
        tier_2_max_mileage: parseFloat(fullDayRates.tier2MaxMileage),
        tier_2_flat_rate: parseFloat(fullDayRates.tier2Rate),
        tier_3_max_mileage: parseFloat(fullDayRates.tier3MaxMileage),
        tier_3_flat_rate: parseFloat(fullDayRates.tier3Rate)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Full-day rates saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving full-day rates:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save rates' });
    } finally {
      setIsSaving(false);
    }
  };

  // Combined save for onboarding flow
  const handleSaveAllAndContinue = async () => {
    // Validate half-day rates
    const halfDayError = validateRates(halfDayRates, 'half-day');
    if (halfDayError) {
      setMessage({ type: 'error', text: halfDayError });
      return;
    }

    // Validate full-day rates
    const fullDayError = validateRates(fullDayRates, 'full-day');
    if (fullDayError) {
      setMessage({ type: 'error', text: fullDayError });
      return;
    }

    try {
      setIsSaving(true);

      // Save half-day rates
      await axios.post(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/rates`, {
        rate_type: 'half_day',
        tier_1_max_mileage: parseFloat(halfDayRates.tier1MaxMileage),
        tier_1_flat_rate: parseFloat(halfDayRates.tier1Rate),
        tier_2_max_mileage: parseFloat(halfDayRates.tier2MaxMileage),
        tier_2_flat_rate: parseFloat(halfDayRates.tier2Rate),
        tier_3_max_mileage: parseFloat(halfDayRates.tier3MaxMileage),
        tier_3_flat_rate: parseFloat(halfDayRates.tier3Rate)
      });

      // Save full-day rates
      await axios.post(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/rates`, {
        rate_type: 'full_day',
        tier_1_max_mileage: parseFloat(fullDayRates.tier1MaxMileage),
        tier_1_flat_rate: parseFloat(fullDayRates.tier1Rate),
        tier_2_max_mileage: parseFloat(fullDayRates.tier2MaxMileage),
        tier_2_flat_rate: parseFloat(fullDayRates.tier2Rate),
        tier_3_max_mileage: parseFloat(fullDayRates.tier3MaxMileage),
        tier_3_flat_rate: parseFloat(fullDayRates.tier3Rate)
      });

      setMessage({ type: 'success', text: 'All rates saved! Redirecting...' });
      setTimeout(() => {
        navigate('/driver/onboarding');
      }, 1000);
    } catch (error) {
      console.error('Error saving rates:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save rates' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading day rate settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(isOnboarding ? '/driver/onboarding' : '/driver/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              ← {isOnboarding ? 'Back to Onboarding' : 'Back to Dashboard'}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Day Rate Pricing</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Message Banner */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Toggle Acceptance */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 sm:mb-2">Accept Day Bookings</h2>
            <p className="text-gray-600 text-sm">
              Enable this to allow riders to book you for half-day (6 hours) or full-day (12 hours) periods
            </p>
          </div>
          <button
            onClick={handleToggleAcceptance}
            className={`relative inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full transition-colors ${
              acceptsDayBookings ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                acceptsDayBookings ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Half-Day Rates (6 hours) */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Half-Day Rates (6 hours)</h2>
        <p className="text-gray-600 text-sm mb-6">
          Set three pricing tiers based on total mileage. Riders will automatically be charged the lowest tier that accommodates their total mileage.
        </p>

        <div className="space-y-4">
          {/* Tier 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 1: Max Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={halfDayRates.tier1MaxMileage}
                onChange={(e) => setHalfDayRates({...halfDayRates, tier1MaxMileage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 1: Flat Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={halfDayRates.tier1Rate}
                onChange={(e) => setHalfDayRates({...halfDayRates, tier1Rate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 150.00"
              />
            </div>
          </div>

          {/* Tier 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 2: Max Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={halfDayRates.tier2MaxMileage}
                onChange={(e) => setHalfDayRates({...halfDayRates, tier2MaxMileage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 2: Flat Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={halfDayRates.tier2Rate}
                onChange={(e) => setHalfDayRates({...halfDayRates, tier2Rate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 250.00"
              />
            </div>
          </div>

          {/* Tier 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 3: Max Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={halfDayRates.tier3MaxMileage}
                onChange={(e) => setHalfDayRates({...halfDayRates, tier3MaxMileage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 150"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 3: Flat Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={halfDayRates.tier3Rate}
                onChange={(e) => setHalfDayRates({...halfDayRates, tier3Rate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 350.00"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSaveHalfDay}
          disabled={isSaving}
          className="mt-6 w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Half-Day Rates'}
        </button>
      </div>

      {/* Full-Day Rates (12 hours) */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Full-Day Rates (12 hours)</h2>
        <p className="text-gray-600 text-sm mb-6">
          Set three pricing tiers based on total mileage. Riders will automatically be charged the lowest tier that accommodates their total mileage.
        </p>

        <div className="space-y-4">
          {/* Tier 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 1: Max Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={fullDayRates.tier1MaxMileage}
                onChange={(e) => setFullDayRates({...fullDayRates, tier1MaxMileage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 1: Flat Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={fullDayRates.tier1Rate}
                onChange={(e) => setFullDayRates({...fullDayRates, tier1Rate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 300.00"
              />
            </div>
          </div>

          {/* Tier 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 2: Max Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={fullDayRates.tier2MaxMileage}
                onChange={(e) => setFullDayRates({...fullDayRates, tier2MaxMileage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 2: Flat Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={fullDayRates.tier2Rate}
                onChange={(e) => setFullDayRates({...fullDayRates, tier2Rate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 500.00"
              />
            </div>
          </div>

          {/* Tier 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 3: Max Mileage
              </label>
              <input
                type="number"
                step="0.01"
                value={fullDayRates.tier3MaxMileage}
                onChange={(e) => setFullDayRates({...fullDayRates, tier3MaxMileage: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tier 3: Flat Rate ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={fullDayRates.tier3Rate}
                onChange={(e) => setFullDayRates({...fullDayRates, tier3Rate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 700.00"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSaveFullDay}
          disabled={isSaving}
          className="mt-6 w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Full-Day Rates'}
        </button>
      </div>

      {/* Onboarding Navigation */}
      {isOnboarding && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-green-800">Almost Done!</h3>
              <p className="text-sm text-green-600">Save your day rates to complete this onboarding step.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/driver/onboarding')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-center"
              >
                Back to Onboarding
              </button>
              <button
                onClick={handleSaveAllAndContinue}
                disabled={isSaving}
                className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-center"
              >
                {isSaving ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
      </main>
    </div>
  );
};

export default DayRatePricing;
